import { useState } from "react";
import { FileText, ExternalLink, Link2, Check, BookOpen, Phone, Mail } from "lucide-react";
import { BRAND } from "../../lib/brandConfig";

const DOC_ICON_TONE = {
  "Relocation Guide": "bg-[#0F6E6E]/10 text-[#0F6E6E]",
  Marketing: "bg-[#C8A96B]/15 text-[#B5915A]",
  Agreement: "bg-slate-100 text-slate-600",
  Pricing: "bg-emerald-50 text-emerald-700",
  Other: "bg-blue-50 text-blue-700",
};

const ToolkitCard = ({ doc }) => {
  const [copied, setCopied] = useState(false);
  const safeUrl = /^https?:\/\//i.test(doc.url || "") ? doc.url : null;
  const share = async () => {
    if (!safeUrl) return;
    try { await navigator.clipboard.writeText(safeUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { window.prompt("Copy this link:", safeUrl); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col" data-testid={`portal-doc-${doc.id}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${DOC_ICON_TONE[doc.doc_type] || DOC_ICON_TONE.Other}`}>
        <FileText className="w-5 h-5" />
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{doc.doc_type}</div>
      <h3 className="mt-1 font-bold text-[#0A2342] leading-snug">{doc.title}</h3>
      {doc.notes && <p className="mt-1 text-xs text-slate-500 line-clamp-3">{doc.notes}</p>}
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2">
        {safeUrl ? (
          <>
            <a href={safeUrl} target="_blank" rel="noreferrer" data-testid={`portal-doc-link-${doc.id}`}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#0A2342] text-white text-xs font-semibold hover:bg-[#1F2A44]">
              View <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button onClick={share} data-testid={`portal-doc-share-${doc.id}`}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-[#0A2342] hover:bg-slate-50">
              {copied ? <><Check className="w-3.5 h-3.5 text-emerald-600" /> Copied</> : <><Link2 className="w-3.5 h-3.5" /> Share link</>}
            </button>
          </>
        ) : <span className="text-xs text-slate-400">Link not available</span>}
      </div>
    </div>
  );
};

export const PortalToolkit = ({ documents }) => (
  <section id="toolkit" className="scroll-mt-6" data-testid="portal-toolkit">
    <div className="flex items-end justify-between gap-3 mb-4">
      <div>
        <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold">Resources</p>
        <h2 className="mt-1 text-lg md:text-xl font-bold text-[#0A2342] font-['Playfair_Display'] flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#C8A96B]" /> Relocation Toolkit
        </h2>
      </div>
    </div>
    {documents.length === 0 ? (
      <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="portal-toolkit-empty">
        Resources for your team will appear here as soon as they're shared with your company.
      </div>
    ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portal-documents-list">
        {documents.map((d) => <ToolkitCard key={d.id} doc={d} />)}
      </div>
    )}
  </section>
);

export const PortalHelp = ({ persona }) => (
  <section className="bg-[#0A2342] rounded-2xl p-6 md:p-10 text-white" data-testid="portal-help">
    <h2 className="text-xl md:text-2xl font-bold font-['Playfair_Display']">Need Help With {persona?.type === "corporate" ? "a Relocating Employee" : persona?.type === "professional" ? "a Relocating Client" : "Someone Relocating"}?</h2>
    <p className="mt-2 text-sm text-white/75 max-w-xl">
      Bryan Spann · Urban Nest Quest | {BRAND.brokerage}. Reach out directly — no ticket queue, just a conversation.
    </p>
    <div className="mt-6 flex flex-col sm:flex-row gap-3">
      <a href="tel:9362241484" data-testid="portal-help-call"
        className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#C8A96B] hover:bg-[#B5915A] text-[#0A2342] font-bold">
        <Phone className="w-4 h-4" /> Call Bryan · 936-224-1484
      </a>
      <a href="mailto:Bryan@UrbanNestQuest.com" data-testid="portal-help-email"
        className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-white/30 hover:bg-white/10 font-semibold">
        <Mail className="w-4 h-4" /> Email Bryan
      </a>
    </div>
  </section>
);
